import {useContext, useRef, useState, type CSSProperties, type ReactNode, type Ref} from 'react';
import {tr} from '../i18n/idioma';
import {ContextoPantallaCompleta} from './pantalla-completa';

const ALTO_MINIMO = 160;
const PASO_CON_TECLADO = 40;

// Caja con desplazamiento para una tabla, con una barra en el borde de abajo
// para cambiar su alto arrastrando (o con las flechas). Doble clic en la barra
// vuelve al alto de siempre. A pantalla completa, el alto de siempre es casi
// toda la pantalla.
export function CajaAjustable({
  ref,
  altoMaximo,
  className,
  style,
  children,
}: {
  ref?: Ref<HTMLDivElement>;
  altoMaximo?: string;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}) {
  const {completa, alternar} = useContext(ContextoPantallaCompleta);
  const [alto, setAlto] = useState<number | null>(null);
  const caja = useRef<HTMLDivElement | null>(null);

  const conectar = (elemento: HTMLDivElement | null) => {
    caja.current = elemento;
    if (typeof ref === 'function') ref(elemento);
    else if (ref) ref.current = elemento;
  };
  const altoActual = () => caja.current?.getBoundingClientRect().height ?? 300;

  const arrastrar = (evento: React.PointerEvent) => {
    evento.preventDefault();
    const inicio = evento.clientY;
    const desde = altoActual();
    const mover = (e: PointerEvent) => setAlto(Math.max(ALTO_MINIMO, desde + e.clientY - inicio));
    const soltar = () => {
      window.removeEventListener('pointermove', mover);
      window.removeEventListener('pointerup', soltar);
    };
    window.addEventListener('pointermove', mover);
    window.addEventListener('pointerup', soltar);
  };

  const estilo: CSSProperties =
    alto !== null
      ? {...style, height: alto, maxHeight: 'none'}
      : {...style, maxHeight: completa ? 'calc(100vh - 9rem)' : altoMaximo};

  return (
    <div>
      {alternar && (
        <div className="mb-1 flex justify-end">
          <button
            type="button"
            onClick={alternar}
            className="rounded-md border border-linea bg-white px-3 py-1 text-sm hover:bg-papel"
          >
            {completa
              ? tr('✕ Salir de pantalla completa (Esc)', '✕ Exit full screen (Esc)')
              : tr('⛶ Pantalla completa', '⛶ Full screen')}
          </button>
        </div>
      )}
      <div ref={conectar} style={estilo} className={className}>
        {children}
      </div>
      <div
        role="separator"
        aria-orientation="horizontal"
        aria-label={tr('Cambiar el alto de la tabla', 'Resize the table height')}
        title={tr(
          'Arrastra para cambiar el alto de la tabla (doble clic: alto normal)',
          'Drag to change the table height (double-click: normal height)',
        )}
        tabIndex={0}
        onPointerDown={arrastrar}
        onDoubleClick={() => setAlto(null)}
        onKeyDown={e => {
          if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
          e.preventDefault();
          const cambio = e.key === 'ArrowDown' ? PASO_CON_TECLADO : -PASO_CON_TECLADO;
          setAlto(Math.max(ALTO_MINIMO, altoActual() + cambio));
        }}
        className="group flex h-3 cursor-ns-resize touch-none items-center justify-center focus:outline-none"
      >
        <span className="h-1 w-14 rounded-full bg-linea group-hover:bg-verde group-focus:bg-verde" />
      </div>
    </div>
  );
}
